import React, { FC, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Loader, Typography } from 'fronton-react';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { cx } from 'linaria';
import {
  getRegionId,
  getTitle,
  getBreadcrumbs,
  getShowStorefrontPics,
} from 'features/page-model/ducks/get-model';
import {
  getBuildingYardOperationalTime,
  getGardenCenterOperationalTime,
  getImageLink,
  getIsPickupPoint,
  getStoreDetail,
} from 'features/store/ducks/get-store-by-id';
import { MapSingleStore } from 'features/map-single-store';
import { StoreSchedule } from 'features/store-common-info/store-schedule';
import { StoreAddress } from 'features/store-common-info/store-adress';
import { NearestSubway } from 'features/store-common-info/store-subway';
import { addNbhp } from 'utils/add-nbhp';
import { displayNone, hideScrollbar, loader } from 'common/styles';
import { StoreConveniences } from './store-conveniences';
import {
  addressInfo,
  criticalNotification,
  greyContainer,
  MainInfoContainer,
  mainInfoHead,
  metroInfo,
  overScrollContainer,
  scrollContainer,
  smallMap,
  storeConveniences,
} from './styles';
import { StorePlanMobile } from './store-plan-mobile';
import { StoreFrontImage } from '../store-front-image';
import { StoreNotifications } from './store-notifications';
import { StoreGardenCenterBuildingYard } from './store-garden-center-building-yard';

export const DATA_QA_MAP_SINGLE_STORE_MOBILE = 'map-single-store-mobile';

export const MainInfo: FC = () => {
  const {
    address,
    nearestSubwayStation = '',
    geocoordinateLatitude,
    geocoordinateLongitude,
    companyId,
    b2cPhone,
    criticalNotificationTitle,
    ...storeDetail
  } = useSelector(getStoreDetail) || {};
  const { link } = useSelector(getBreadcrumbs)?.[2] || {};
  const regionId = useSelector(getRegionId);
  const { smMobile, mobile } = useBreakpoints() ?? {};
  const title = useSelector(getTitle);
  const imageLink = useSelector(getImageLink);
  const showStoreFrontImage = !!useSelector(getShowStorefrontPics);
  const mapClick = useCallback(() => {
    if (companyId) {
      window.open(`https://maps.yandex.ru/org/lerua_merlen/${companyId}`, '_blank');
    }
  }, [companyId]);
  const isPickupPoint = useSelector(getIsPickupPoint);
  const gardenCenterOperationalTime = useSelector(getGardenCenterOperationalTime);
  const buildingYardOperationalTime = useSelector(getBuildingYardOperationalTime);
  const [mapLoaded, setMapLoaded] = useState(false);

  return (
    <MainInfoContainer data-qa="main-info">
      {title && (
        <Typography variant="h1" as="h1" data-qa="shop-title" color="text-primary" className={mainInfoHead}>
          {addNbhp(title)}
        </Typography>
      )}
      {!criticalNotificationTitle && <StoreSchedule storeDetail={storeDetail} name={title} />}
      <StoreNotifications className={criticalNotification} />
      {(smMobile || mobile) && (
        <div className={overScrollContainer} data-qa="main-info-mobile-carousel">
          <div className={cx(scrollContainer, hideScrollbar)}>
            {geocoordinateLatitude && geocoordinateLongitude && (
              <div className={cx(smallMap, greyContainer)} data-qa={DATA_QA_MAP_SINGLE_STORE_MOBILE}>
                {!mapLoaded && <Loader size="m" className={loader} />}
                <MapSingleStore
                  setMapLoaded={setMapLoaded}
                  activeRegionId={regionId}
                  latitude={geocoordinateLatitude}
                  longitude={geocoordinateLongitude}
                  disable
                  onClick={mapClick}
                />
              </div>
            )}
            <StorePlanMobile className={greyContainer} />
            {showStoreFrontImage && imageLink && (
              <div className={greyContainer}>
                <StoreFrontImage imageUrl={imageLink} />
              </div>
            )}
          </div>
        </div>
      )}
      {address && <StoreAddress className={addressInfo} blockName={title} />}
      {b2cPhone && <meta content={b2cPhone} />}
      {!!nearestSubwayStation.length && <NearestSubway className={metroInfo} />}
      {(gardenCenterOperationalTime || buildingYardOperationalTime) && (
        <StoreGardenCenterBuildingYard
          gardenCenterOperationalTime={gardenCenterOperationalTime}
          buildingYardOperationalTime={buildingYardOperationalTime}
          gaName={addNbhp(title)}
        />
      )}
      {!isPickupPoint && <StoreConveniences className={storeConveniences} />}
      <div className={displayNone} itemType="https://schema.org/URL">
        {link}
      </div>
      <div className={displayNone} itemType="https://schema.org/GeoCoordinates">
        {geocoordinateLatitude}
      </div>
      <div className={displayNone} itemType="https://schema.org/GeoCoordinates">
        {geocoordinateLongitude}
      </div>
    </MainInfoContainer>
  );
};
